import { chakra, type HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, type ReactNode } from 'react';
import { buttonVariant, focusRing, type ButtonVariant } from './_shared';

const Base = chakra('button', {
  base: {
    position: 'fixed',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSize: '14',
    borderRadius: 'full',
    boxShadow: 'lg',
    cursor: 'pointer',
    fontFamily: 'body',
    fontSize: 'xl',
    zIndex: 'sticky',
    transitionProperty: 'background-color, border-color, color, filter, box-shadow',
    transitionDuration: 'fast',
    transitionTimingFunction: 'standard',
    _hover: { boxShadow: 'xl' },
    _disabled: { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' },
    ...focusRing,
  },
});

export interface FabProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'aria-label'> {
  /** Required — a floating, icon-only action must be labelled for screen readers. */
  'aria-label': string;
  /** The glyph rendered inside (use `<Icon as={Icons.X} />`). */
  icon: ReactNode;
  variant?: ButtonVariant;
  /** Which screen corner to pin to (default bottom-right). */
  placement?: 'bottom-right' | 'bottom-left';
}

/**
 * A Floating Action Button: a fixed, circular, elevated primary action pinned to a
 * screen corner (the launcher pattern). Icon-only, so an `aria-label` is required.
 * Sits above page content but below modal surfaces (`zIndex: sticky`).
 */
export const Fab = forwardRef<HTMLButtonElement, FabProps>(function Fab(
  { icon, variant = 'primary', placement = 'bottom-right', type = 'button', ...rest },
  ref,
) {
  const sx = { ...buttonVariant[variant] } as HTMLChakraProps<'button'>;
  const corner =
    placement === 'bottom-left'
      ? { left: { base: '4', md: '6' } }
      : { right: { base: '4', md: '6' } };
  return (
    <Base
      ref={ref}
      type={type}
      // Below `md` the app shell shows a bottom tab bar (BottomNav: 56px + iOS
      // safe-area); lift the FAB clear of it on mobile. On >= md it sits in the gutter.
      bottom={{ base: 'calc(56px + env(safe-area-inset-bottom) + 16px)', md: '6' }}
      {...corner}
      {...sx}
      {...rest}
    >
      {icon}
    </Base>
  );
});
